import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  Flex,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaLeaf } from "react-icons/fa";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionType,
  type AwardCriterionDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
  WeightType,
} from "../../../../../autogen/bff-api";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { TipTap, isTiptapContentEmpty } from "../../../../../common/input/TipTap/TipTap";
import {
  criterionTypeLabelKey,
  criterionTypeSelectValue,
  emptyCriterion,
  ENVIRONMENT_EXEMPTION_TYPES,
  isEnvironmentCriterionType,
  withCriterionType,
} from "../../../../../common/procurement/publicProcurementOptions";
import { exemptionTitleKey } from "../utils/environmentJustificationTemplates";
import { FormSectionBand } from "../../../../../common/library/fields/FormSectionBand";
import { CriterionTypePicker } from "./CriterionTypePicker";
import { WeightInput } from "../../../../../common/award-criteria/WeightInput";
import { RequirementDialogHeader } from "../../../../../common/components/RequirementDialogHeader";

// t('Type') t('Name') t('Weight') t('Description') t('Save') t('Cancel')
// t('New criterion') t('Add criterion') t('E.g. Lowest price wins')
// t('Fill in the type, name and description to add the criterion')
// t('Weight {{weight}} % of the total') t('No environmental weighting')
// t('Change type') t('Keep the type') t('{{count}} sub-criterion')
// t('A sub-criterion takes its type from the criterion, so a change here re-scores every one of them.')
// t('Options')

interface Props {
  isOpen: boolean;
  eventId: string;
  /**
   * "add" collects a whole criterion before anything is written, so an abandoned attempt leaves no row
   * behind and a new row is never born missing its description. "edit" writes every keystroke straight
   * through, which is what the row beside it does too.
   */
  mode: "add" | "edit";
  /** Position in the criteria list, so the drawer can surface the same field errors as the row. */
  index: number | null;
  isLocked: boolean;
  criterion: AwardCriterionDto | null;
  /** True when some *other* criterion is the price one, which makes Price unavailable here. */
  hasOtherPriceCriterion: boolean;
  inOrderOfPriority?: boolean;
  onChange: (next: AwardCriterionDto) => void;
  /** Add mode only: hands over the finished criterion. The caller appends it and closes the drawer. */
  onSubmit?: (next: AwardCriterionDto) => void;
  /**
   * Add mode only: the "keep this for later" row, shown under the fields. Owned by the step, which is
   * where the library write happens — this drawer stays presentational.
   */
  saveToLibraryOption?: ReactNode;
  /** Enters a § 5c "no environmental weighting" exemption; present only for environment criteria. */
  onChooseExemption?: (exemptionType: AwardCriterionType) => void;
  onClose: () => void;
}

const BLANK_TYPE = "";
const MIN_WEIGHT = 1;
const MAX_WEIGHT = 100;

/**
 * How the type reads in edit mode. It starts settled because by then the criterion has a name, a
 * description and possibly requirements, all authored for the type it already has — a row of pills
 * sitting open invites the one accidental click that reinterprets all of it.
 */
type TypeEditState = "settled" | "confirming" | "choosing";

export const AwardCriterionDrawer = ({
  isOpen,
  eventId,
  mode,
  index,
  isLocked,
  criterion,
  hasOtherPriceCriterion,
  inOrderOfPriority = false,
  onChange,
  onSubmit,
  saveToLibraryOption,
  onChooseExemption,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const formatFieldError = useFieldErrorTranslation();
  const { fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const weightFieldError =
    index == null
      ? undefined
      : fieldError(`eventData.awardCriteria.bestPriceQualityRatio.criteria.${index}.weight.number`);

  const isAdding = mode === "add";
  // The draft never reaches the event until it is submitted, which is the whole point of add mode. The
  // type starts unpicked so the buyer chooses it rather than inheriting whichever default we guessed.
  const [draft, setDraft] = useState<AwardCriterionDto | null>(null);
  const [typeValue, setTypeValue] = useState<string>(BLANK_TYPE);
  const [typeEdit, setTypeEdit] = useState<TypeEditState>("settled");

  // Editing writes every keystroke straight through, so "Cancel" has to put something back rather than
  // just close: the criterion as it stood when the panel was opened.
  const latest = useRef(criterion);
  latest.current = criterion;
  const opened = useRef<AwardCriterionDto | null>(null);

  const typeRef = useRef<HTMLButtonElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    // Every open starts from the type as it stands, so a change abandoned last time is not still
    // half-open the next time the panel is used.
    setTypeEdit("settled");
    opened.current = latest.current;
    if (!isAdding) return;
    setDraft(null);
    setTypeValue(BLANK_TYPE);
  }, [isAdding, isOpen]);

  const working = isAdding ? draft : criterion;

  const cancel = () => {
    if (!isAdding && opened.current) onChange(opened.current);
    onClose();
  };

  const applyChange = (next: AwardCriterionDto) => (isAdding ? setDraft(next) : onChange(next));

  const applyType = (value: string) => {
    setTypeValue(value);
    setDraft((prev) => (prev ? withCriterionType(prev, value) : emptyCriterion(value)));
  };

  if (!isAdding && !criterion) return null;

  // While adding, the subtitle says what the panel still wants — picking a type is the first of three
  // steps, not the end of them, so it must not flip to the criterion's own summary the moment a type
  // creates the draft.
  const subtitle = isAdding
    ? t("Fill in the type, name and description to add the criterion")
    : inOrderOfPriority
      ? t("Priority {{rank}}", { rank: working?.weight?.number ?? "—" })
      : // The type is not repeated here: it has its own label below, which is also where it is changed.
        t("Weight {{weight}} % of the total", { weight: working?.weight?.number ?? "—" });

  const subCriteriaCount = (working?.subCriteria ?? []).length;
  const hasSubCriteria = subCriteriaCount > 0;

  const weightNumber = working?.weight?.number;
  const weightOutOfRange = weightNumber != null && (weightNumber < MIN_WEIGHT || weightNumber > MAX_WEIGHT);
  const weightError = weightFieldError
    ? formatFieldError(weightFieldError)
    : weightOutOfRange
      ? t("Must be between 1 and 100")
      : null;

  // The publish gate needs a description, and the weights are distributed across criteria afterwards —
  // so those three are what a criterion cannot be born without, and the weight deliberately is not.
  const canSubmit =
    working != null &&
    typeValue !== BLANK_TYPE &&
    (working.name?.trim() ?? "") !== "" &&
    !isTiptapContentEmpty(working.description) &&
    !weightOutOfRange;

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
      initialFocusRef={isAdding ? typeRef : nameRef}
    >
      <DrawerOverlay />
      <DrawerContent data-testid="award-criterion-drawer">
        <RequirementDialogHeader
          title={isAdding ? t("New criterion") : working?.name?.trim() || t(working?.type ?? "")}
          subtitle={subtitle}
        />

        <DrawerBody>
          <FormControl mb="6" isRequired={isAdding}>
            <FormLabel>{t("Type")}</FormLabel>
            {isAdding || typeEdit === "choosing" ? (
              <Flex direction="column" align="flex-start" gap="2">
                <CriterionTypePicker
                  value={working ? criterionTypeSelectValue(working.type) : typeValue}
                  hasOtherPriceCriterion={hasOtherPriceCriterion}
                  isDisabled={isLocked}
                  firstOptionRef={typeRef}
                  onSelect={(value) => {
                    // The name field is disabled until a type creates the draft, so it can only take
                    // focus once that render has committed.
                    requestAnimationFrame(() => nameRef.current?.focus());
                    if (isAdding) return applyType(value);
                    if (working) applyChange(withCriterionType(working, value));
                    setTypeEdit("settled");
                  }}
                />
                {!isAdding && (
                  // Not "Cancel": the footer now owns that word for the whole panel, and backing out
                  // here only means the type stays as it was.
                  <Button variant="link" size="xs" onClick={() => setTypeEdit("settled")}>
                    {t("Keep the type")}
                  </Button>
                )}
              </Flex>
            ) : (
              <Flex align="center" gap="3">
                {/* The same badge the table row uses for the type, so one fact looks like itself in
                    both places — a size up, because this is the surface it is edited from. */}
                <Badge
                  data-testid="criterion-type"
                  bg="smBackgroundSecondary"
                  color="smMuted"
                  px="2"
                  py="1"
                  borderRadius="md"
                  textTransform="none"
                  fontSize="xs"
                  fontWeight="medium"
                >
                  {working ? t(criterionTypeLabelKey(working.type)) : null}
                </Badge>
                {!isLocked && typeEdit === "settled" && (
                  <Button
                    variant="link"
                    size="xs"
                    colorScheme="teal"
                    onClick={() => setTypeEdit(hasSubCriteria ? "confirming" : "choosing")}
                  >
                    {t("Change type")}
                  </Button>
                )}
              </Flex>
            )}

            {/* Named consequence rather than a vague warning: a sub-criterion has no type of its own,
                so changing the criterion's re-scores every one of them. */}
            {typeEdit === "confirming" && (
              <Box mt="3" p="3" borderWidth="1px" borderColor="smBorder" borderRadius="md" bg="smBackgroundSecondary">
                {/* The count on its own line, through the plural key that already exists — folding it
                    into the sentence would read "all 1 change with it" for a single sub-criterion. */}
                <Text fontSize="sm" fontWeight="semibold" color="smText">
                  {t("{{count}} sub-criterion", { count: subCriteriaCount })}
                </Text>
                <Text fontSize="sm" color="smMuted">
                  {t("A sub-criterion takes its type from the criterion, so a change here re-scores every one of them.")}
                </Text>
                <Flex gap="3" mt="3">
                  <Button variant="outline" size="xs" onClick={() => setTypeEdit("settled")}>
                    {t("Keep the type")}
                  </Button>
                  <Button colorScheme="teal" size="xs" onClick={() => setTypeEdit("choosing")}>
                    {t("Change type")}
                  </Button>
                </Flex>
              </Box>
            )}
          </FormControl>

          <Flex gap="4" mb="6" align="flex-start">
            <FormControl isDisabled={isLocked || !working} isRequired={isAdding} flex="1">
              <FormLabel>{t("Name")}</FormLabel>
              <Input
                ref={nameRef}
                rounded="md"
                autoComplete="off"
                data-1p-ignore
                value={working?.name ?? ""}
                placeholder={t("E.g. Lowest price wins") ?? ""}
                onChange={(e) => working && applyChange({ ...working, name: e.target.value })}
              />
            </FormControl>

            {!inOrderOfPriority && (
              <FormControl isInvalid={weightError != null} w="24" flexShrink={0}>
                <FormLabel>{t("Weight")}</FormLabel>
                <WeightInput
                  value={working?.weight?.number}
                  isDisabled={isLocked || !working}
                  isInvalid={weightError != null}
                  ariaLabel={t("Weight")}
                  onChange={(weight) =>
                    working &&
                    applyChange({
                      ...working,
                      weight: weight == null ? undefined : { number: weight, type: WeightType.Percentage },
                    })
                  }
                />
                {weightError && <FormErrorMessage fontSize="xs">{weightError}</FormErrorMessage>}
              </FormControl>
            )}
          </Flex>

          <Box mb="6">
            <Text fontSize="sm" fontWeight="medium" mb="2">
              {t("Description")}
              <Text as="span" color="smWarning" ml="1">
                *
              </Text>
            </Text>
            <TipTap ariaLabel={t("Description") ?? ""}
              key={isAdding ? typeValue : "edit"}
              content={working?.description ?? ""}
              onUpdate={(value: string) => {
                if (working) applyChange({ ...working, description: value });
              }}
              editable={!isLocked && !!working}
              toolbar={{
                underline: false,
                headings: false,
                colors: false,
                link: false,
                table: false,
                image: false,
                template: false,
              }}
              debounceWait={0}
            />
          </Box>

          {/* The shared rationale is not authored here. It stays on the criterion — the evaluation page
              still prefills from it — but writing an assessment belongs to the evaluation, not to the
              step where the competition is described. */}

          {/* Editing a criterion the procurement already holds is not the moment to decide it is worth
              keeping — the table row's own action is. */}
          {isAdding && saveToLibraryOption && (
            <>
              <FormSectionBand>{t("Options")}</FormSectionBand>
              {saveToLibraryOption}
            </>
          )}

          {!isAdding && working && isEnvironmentCriterionType(working.type) && onChooseExemption && (
            <Flex justify="flex-end">
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="outline"
                  leftIcon={<FaLeaf />}
                  rightIcon={<ChevronDownIcon />}
                  isDisabled={isLocked}
                >
                  {t("No environmental weighting")}
                </MenuButton>
                <MenuList>
                  {ENVIRONMENT_EXEMPTION_TYPES.map((type) => (
                    <MenuItem
                      key={type}
                      onClick={() => {
                        onChooseExemption(type);
                        onClose();
                      }}
                    >
                      {t(exemptionTitleKey(type))}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Flex>
          )}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="smBorder">
          {isAdding ? (
            <Flex gap="3">
              <Button variant="outline" onClick={onClose}>
                {t("Cancel")}
              </Button>
              <Button
                colorScheme="teal"
                isDisabled={!canSubmit}
                onClick={() => working && onSubmit?.(working)}
              >
                {t("Add criterion")}
              </Button>
            </Flex>
          ) : (
            <Flex gap="3">
              <Button variant="outline" onClick={cancel}>
                {t("Cancel")}
              </Button>
              <Button colorScheme="teal" onClick={onClose}>
                {t("Save")}
              </Button>
            </Flex>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
